import React, { useEffect, useRef, useState } from 'react';
import type { InputRef } from 'antd';
import type { LabeledValue } from 'antd/lib/select';
import { Button, Divider, Input, Space, message } from 'antd';
import { ProFormDependency } from '@ant-design/pro-components';
import ProForm, { ProFormSelect, ProFormText } from '@ant-design/pro-form';

import { getDict } from '@/services/basic';
import { fetchCustomPageList } from '@/pages/TenantSettings/CustomizePage/components/PageList/service';
import type { LinkParams } from './data';
import { detailMap, detailPageList, tabPageList, /* systemPageList, */ urlTypeList } from './_data';
import styles from './index.less';
import { PlusOutlined } from '@ant-design/icons';

/** 处理链接类型 1/5 的转换
 * @param convertType   *转换类型，0：1 => 1/5、1：1/5 => 1
 * @param arr           需要转换的数据数组，默认为空数组
 */
export function handleLinkUrlType(convertType: number, arr: Record<string, any>[]) {
  const tabUrlList = tabPageList.map(({ value }) => value);
  return arr?.map(item => {
    const { urlType, url } = item;
    if (convertType === 0) { // 转换类型，0：1 => 1/5
      // 如果是 tab 页，设置 urlType 为 5，用于小程序端页面跳转，系统预设拆分为 1/5
      if (tabUrlList.includes(url)) item.urlType = 5;
    }
    if (convertType === 1) { // 转换类型，1：1/5 => 1
      // 如果是 tab 页，设置 urlType 为 1，用于 web 端组件的链接配置，1/5 合并成系统预设 1
      if (tabUrlList.includes(url)) {
        item.urlType = 1;
        item.systemUrl = item?.url
      }
      // 如果是 h5 链接且 url 存在，避免 h5url 为空,使用 url 设置 h5Url
      if (urlType === 2 && !!url) {
        item.h5Url = url;
      }
    }
    return item;
  });
}

/** 处理链接数据
 * 如果链接数据存在 url 有值而 urlType 为空的情况，
 * 为其增加 urlType: 1/5, systemUrl: url，用于在 ./Tool/index.tsx 中的 LinkComponent 中设值并显示
 */
export function handleLinkData(obj: Record<string, any>, name: string) {
  if (obj?.[name] && Array.isArray(obj[name])) {
    obj[name] = obj[name]?.map((item: any) => {
      if (item?.url && item?.urlType == null) {
        const tabUrlList = tabPageList.map(({ value }) => value);
        return {
          ...item,
          urlType: tabUrlList.includes(item?.url) ? 5 : 1,
          systemUrl: item?.url,
        }
      }
      return item;
    });
  }
}

export type LinkComponentProps = {
  preName?: string;
  initData?: LinkParams;
  setCurrentRowData?: (data?: Record<string, any>) => void;
}
const LinkComponent: React.FC<LinkComponentProps> = ({
  preName = '',
  initData,
  setCurrentRowData,
}) => {
  // 跳转类型为系统预设时，右侧的下拉选择可以添加新选项
  const inputRef = useRef<InputRef>(null);
  const [newUrl, setNewUrl] = useState('');
  // 将系统链接列表拆分为 remoteUrlList、detailList、localUrlList，添加的新选项存入 localUrlList 中
  const [localUrlList, setLocalUrlList] = useState<LabeledValue[]>([]);
  const [remoteUrlList, setRemoteUrlList] = useState<LabeledValue[]>([]);
  const [systemUrlList, setSystemUrlList] = useState<LabeledValue[]>([]);
  const detailList  = detailPageList.map(({ value, defaultMessage }) => ({ value, label: defaultMessage }));
  useEffect(() => {
    getDict({ category: 'mini-app-page-path' })
      .then(({ code, data }) => {
        // 详情类型需要做特殊处理，将接口返回的 -detail 类型的数据筛选掉
        const list: LabeledValue[] = code === 200
          ? data?.filter((item: any) => !item?.code?.includes('-detail'))
            ?.map((item: any) => ({ label: item?.name, value: item?.description })) || []
          : [];
          setRemoteUrlList(list);
      }).catch(err => {
        console.log(err);
      })
  }, []);
  useEffect(() => {
    const { urlType, url } = initData || {};
    // 如果是系统预设且 url 存在，需要判断是否将 url 存入 localUrlList 中
    if (urlType === 1 && !!url) {
      // 如果 url 不存在于 localUrlList 中，则存入
      const list = localUrlList.find(item => item.value === url)
        ? localUrlList
        : [...localUrlList, { value: url, label: url }];
      // 如果链接已存在于 remoteUrl 中，则将其从 localUrlList 中移除，避免重复项
      setLocalUrlList(list?.filter(item => !remoteUrlList.find(({ value }) => value === item.value)));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initData, remoteUrlList]);
  useEffect(() => {
    // 由 remoteUrlList、detailList、localUrlList 合并为 systemUrlList
    setSystemUrlList([
      ...remoteUrlList,
      ...detailList,
      ...localUrlList,
    ]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localUrlList, remoteUrlList]);

  return (
    <>
      <ProForm.Item className={styles.link} label="跳转链接">
        <Space.Compact className={styles.spaceCompact}>
          {/* 链接类型 urlType */}
          <ProFormSelect
            name={[preName, 'urlType'].filter(name => name)}
            showSearch
            options={urlTypeList.map(({ value, defaultMessage }) => ({ value, label: defaultMessage }))}
            fieldProps={{
              defaultValue: initData?.urlType,
              dropdownMatchSelectWidth: false,
              onChange: urlType => {
                const newData = {
                  urlType,
                  systemUrl: undefined,
                  pageId: undefined,
                  h5Url: undefined,
                  detailUrl: undefined,
                  url: undefined,
                  data: undefined,
                };
                setCurrentRowData?.(preName ? { [preName]: newData } : newData);
              },
            }}
          />
          <ProFormDependency name={[preName, 'urlType'].filter(name => name)}>
            {({ [preName]: pn, urlType: type }) => {
              const urlType = pn?.urlType || type;
              return (
                <>
                  {/* 系统预设 1、5 */}
                  {urlType === 1 ? (
                    <ProFormSelect
                      name={[preName, 'systemUrl'].filter(name => name)}
                      colon={false}
                      showSearch
                      placeholder={'请选择页面'}
                      options={systemUrlList}
                      fieldProps={{
                        defaultValue: initData?.systemUrl,
                        placement: 'bottomRight',
                        dropdownMatchSelectWidth: false,
                        onChange: systemUrl => {
                          const newData = {
                            systemUrl,
                            detailUrl: undefined,
                            url: !detailMap?.[systemUrl] ? systemUrl : undefined,
                          };
                          setCurrentRowData?.(preName ? { [preName]: newData } : newData);
                        },
                        dropdownRender: menu => (
                          <>
                            {menu}
                            <Divider style={{ margin: '8px 0' }} />
                            <Space style={{ padding: '0 8px 4px' }}>
                              <Input
                                ref={inputRef}
                                value={newUrl}
                                placeholder="请输入链接"
                                onChange={e => setNewUrl(e.target.value)}
                              />
                              <Button type="primary" icon={<PlusOutlined />} onClick={e => {
                                // 如果 systemUrlList 中不存在新输入的链接，则将其存入 localUrlList 中
                                if (!systemUrlList.find(({ value }) => value === newUrl)) {
                                  setLocalUrlList([
                                    ...localUrlList,
                                    { value: newUrl, label: newUrl },
                                  ]);
                                } else {
                                  message.warning('链接已存在');
                                }
                                setNewUrl('');
                                setTimeout(() => inputRef.current?.focus(), 0);
                              }}>
                                添加
                              </Button>
                            </Space>
                          </>
                        ),
                      }}
                    />
                  ) : null}
                  {/* 自定义页面 */}
                  {urlType === 4 ? (
                    <ProFormSelect
                      name={[preName, 'pageId'].filter(name => name)}
                      colon={false}
                      showSearch
                      placeholder={'请选择页面'}
                      debounceTime={200}
                      request={async () => {
                        const { code, data } = await fetchCustomPageList();
                        return code === 200
                          ? data?.map(({ id, name }: { id: number, name: string }) => ({
                            value: id,
                            label: name,
                          })) || []
                          : []
                      }}
                      fieldProps={{
                        defaultValue: initData?.pageId,
                        placement: 'bottomRight',
                        dropdownMatchSelectWidth: false,
                        onChange: pageId => {
                          const newData = { pageId };
                          setCurrentRowData?.(preName ? { [preName]: newData } : newData);
                        },
                      }}
                    />
                  ) : null}
                  {/* h5 页面 */}
                  {urlType === 2 ? (
                    <ProFormText
                      name={[preName, 'h5Url'].filter(name => name)}
                      colon={false}
                      placeholder={'请输入链接地址'}
                      fieldProps={{
                        defaultValue: initData?.h5Url,
                        onChange: e => {
                          const h5Url = e?.target?.value;
                          const newData = { h5Url, url: h5Url };
                          setCurrentRowData?.(preName ? { [preName]: newData } : newData);
                        },
                      }}
                    />
                  ) : null}
                </>
              )
            }}
          </ProFormDependency>
        </Space.Compact>
        <ProFormDependency name={[preName, 'systemUrl'].filter(name => name)}>
          {/* {({ [preName]: pn, systemUrl: sUrl }) => {
            const systemUrl = pn?.systemUrl || sUrl; */}
          {params => {
            const systemUrl = preName ? params?.[preName]?.systemUrl : params?.systemUrl;
            const detailConfig = detailMap?.[systemUrl];
            if (!detailConfig) return null;
            return (
              <ProFormSelect
                name={[preName, 'detailUrl'].filter(name => name)}
                label=""
                key={detailConfig?.key}
                colon={false}
                showSearch
                debounceTime={200}
                request={async ({ keyWords }) => {
                  const { fetchApi, keywordField, query } = detailConfig;
                  const { code, data } = await fetchApi({ [keywordField]: keyWords, current: 1, pageSize: 999, ...query });
                  return code === 200
                    ? data?.list?.map((item: any) => ({
                      value: detailConfig.url + item.id,
                      label: item?.[detailConfig?.labelField || detailConfig.keywordField],
                    })) || []
                    : []
                }}
                fieldProps={{
                  defaultValue: initData?.detailUrl,
                  placement: 'bottomRight',
                  dropdownMatchSelectWidth: false,
                  onChange: detailUrl => {
                    const newData = { detailUrl, url: detailUrl };
                    setCurrentRowData?.(preName ? { [preName]: newData } : newData);
                  }
                }}
              />
            );
          }}
        </ProFormDependency>
        <ProFormText name={[preName, 'url'].filter(name => name)} hidden fieldProps={{ defaultValue: initData?.url }} />
      </ProForm.Item>

      <ProFormDependency name={[preName, 'urlType'].filter(name => name)}>
        {params => {
          const urlType = preName ? params?.[preName]?.urlType : params?.urlType;
          if (urlType != 3) return null;
          return (
            <>
              <ProFormText
                key={initData?.data?.path}
                name={[preName, 'data', 'appId'].filter(name => name)}
                label="小程序id"
                placeholder={'要打开的小程序appId（百度小程序则填写App Key）'}
                fieldProps={{
                  defaultValue: initData?.data?.appId,
                  onChange: e => {
                    const appId = e?.target?.value;
                    const newData = { data: { ...initData?.data, appId } };
                    console.log(newData.data);
                    setCurrentRowData?.(preName ? { [preName]: newData } : newData);
                  },
                }}
              />
              <ProFormText
                key={initData?.data?.appId}
                name={[preName, 'data', 'path'].filter(name => name)}
                label="页面路径"
                placeholder={'打开的页面路径，如果为空则打开首页'}
                fieldProps={{
                  defaultValue: initData?.data?.path,
                  onChange: e => {
                    const path = e?.target?.value;
                    const newData = { data: { ...initData?.data, path } };
                    console.log(newData.data);
                    setCurrentRowData?.(preName ? { [preName]: newData } : newData);
                  },
                }}
              />
            </>
          );
        }}
      </ProFormDependency>
    </>
  );
}

export default LinkComponent;
